// src/services/apiService.js

/**
 * Convierte las claves de un objeto de snake_case o kebab-case a camelCase de forma recursiva.
 * @param {object | Array | any} data - El objeto o array a procesar.
 * @returns {object | Array | any} El objeto o array con las claves convertidas a camelCase.
 */
const convertKeysToCamelCase = (data) => {
    if (data === null || typeof data !== 'object') {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(item => convertKeysToCamelCase(item));
    }

    return Object.keys(data).reduce((acc, key) => {
        const words = key.split(/[-_]/); // Divide por guiones o guiones bajos

        const camelKey = words.map((word, index) => {
            // Si es la primera palabra...
            if (index === 0) {
                // ...y es una clave de una sola palabra (ej. "ValidUrl"), solo se convierte el primer carácter.
                // Si es la primera de muchas (ej. "valid_url"), se convierte toda la palabra.
                return words.length === 1
                    ? word.charAt(0).toLowerCase() + word.slice(1)
                    : word.toLowerCase();
            }
            // Las palabras subsiguientes: capitaliza la primera letra, el resto en minúsculas
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(''); // Une las palabras para formar la clave camelCase

        acc[camelKey] = convertKeysToCamelCase(data[key]);
        return acc;
    }, {});
};

export const uploadApi = async (url, formData) => {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json'
                // NO definimos Content-Type: multipart/form-data aquí
            },
            credentials: 'include',
            body: formData
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
        }

        const jsonData = await response.json();
        return convertKeysToCamelCase(jsonData);
    } catch (error) {
        console.error("Error in uploadApi call:", error);
        throw error;
    }
};

/**
 * Realiza una petición GET a una URL específica, con la opción de añadir parámetros de consulta.
 * Las propiedades del objeto de respuesta JSON se convertirán a camelCase.
 * @param {string} url - La URL del endpoint de la API (puede ser relativa como "/api/resource").
 * @param {object} [queryParams={}] - Un objeto con los parámetros de consulta (ej. { key: 'value' }).
 * @returns {Promise<object>} Una promesa que resuelve con la respuesta JSON de la API con propiedades en camelCase.
 * @throws {Error} Si la respuesta de la red no es exitosa (status 2xx).
 */
export const getApi = async (url, queryParams = {}) => {
    let fullUrlObject = new URL(url, window.location.origin);

    if (Object.keys(queryParams).length > 0) {
        Object.keys(queryParams).forEach(key => {
            fullUrlObject.searchParams.append(key, queryParams[key]);
        });
    }

    let relativePathWithQuery = fullUrlObject.pathname + fullUrlObject.search;

    if (process.env.NODE_ENV === 'development') {
      relativePathWithQuery = fullUrlObject.origin + relativePathWithQuery;
    }

    try {
        const response = await fetch(relativePathWithQuery, {
            method: 'GET',
            headers: {
                'Accept': 'application/json', // Indicamos que esperamos JSON de vuelta
                'Content-Type': 'application/json'
            },
            credentials: 'include', // Clave para la autenticación por sesión (envía cookies)
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorBody = await response.json();
                if (errorBody && errorBody.message) {
                    errorMessage = errorBody.message;
                }
            } catch (parseError) {
                console.warn("Could not parse error response as JSON:", parseError);
            }
            throw new Error(errorMessage);
        }

        const jsonData = await response.json();
        return convertKeysToCamelCase(jsonData); // Convertir claves a camelCase
    } catch (error) {
        console.error("Error in getApi call:", error);
        throw error; // Re-lanza el error para que el componente lo maneje
    }
};


/**
 * Realiza una petición POST a una URL específica con un cuerpo JSON.
 * Las propiedades del objeto de respuesta JSON se convertirán a camelCase.
 * @param {string} url - La URL del endpoint de la API.
 * @param {object} bodyData - El objeto de datos a enviar en el body de la petición.
 * @returns {Promise<object>} Una promesa que resuelve con la respuesta JSON de la API con propiedades en camelCase.
 * @throws {Error} Si la respuesta de la red no es exitosa (status 2xx).
 */
export const postApi = async (url, bodyData) => {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include', // Clave para la autenticación por sesión (envía cookies)
            body: JSON.stringify(bodyData)
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorBody = await response.json();
                if (errorBody && errorBody.message) {
                    errorMessage = errorBody.message;
                }
            } catch (parseError) {
                console.warn("Could not parse error response as JSON:", parseError);
            }
            throw new Error(errorMessage);
        }

        const jsonData = await response.json();
        return convertKeysToCamelCase(jsonData); // Convertir claves a camelCase
    } catch (error) {
        console.error("Error in postApi call:", error);
        throw error;
    }
};